class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute("handle");
    this.swiperInitialized = false; // Prevent multiple initializations
    this.initBound = this.init.bind(this);
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Prevent multiple initializations
    if (this.swiperInitialized) return;
    if (typeof Swiper === 'undefined') return;
    this.swiperInitialized = true;

    this.options = this.setOptions();
    this.instance = new Swiper('.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", this.initBound);
      window.addEventListener("shopify:section:select", this.initBound);

      // fallback initialization after delay in design mode
      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.initBound, { once: true });
    } else {
      this.init();
    }

    if (!this.swiperInitialized) {
      window.addEventListener('load', this.initBound, { once: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('DOMContentLoaded', this.initBound);
    window.removeEventListener("shopify:section:load", this.initBound);
    window.removeEventListener("shopify:section:select", this.initBound);
    window.removeEventListener('load', this.initBound);
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Options before initialization
   */
  setOptions() {
    if (this.debug) console.log('setOptions');
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    if (this.debug) console.log('getOptionsAsJsonScripts');
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};
    if (jsonScriptAutomated && jsonScriptAutomated.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptAutomated.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptAutomated?.textContent);
      }
    }
    if (jsonScriptOverwrite && jsonScriptOverwrite.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptOverwrite.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptOverwrite?.textContent);
      }
    }
    return options;
  }

  /** thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.debug) console.log('setInteractions');
  }
}
// customElements.define('instance-swiper', InstanceSwiper);
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;
    if (this.debug) console.log('setCenteredSlides');

    const afterInit = (swiper) => {
      const selectedSlideIndex = Array
        .from(this.querySelectorAll(".swiper-slide"))
        .findIndex(slide => slide.hasAttribute("data-selected"));
      const initialIndex = selectedSlideIndex > -1 ? selectedSlideIndex : 0;
      if (swiper.activeIndex !== initialIndex) swiper.slideTo(initialIndex, 0);
      if (this.debug) console.log('afterInit');
      const swiperWrapper = swiper.wrapperEl;
      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });
      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
          if (this.debug) console.log('swiperWrapper observer removed styles!');
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
              if (this.debug) console.log('swiperWrapper observer disconnect!');
            }
          }, 500);
        }
      }

      /**
       * Quick fix for thumbnails dislocationing issue
       * when thumbnails are less than the height of the wrapper
       */
      const calculateAllThumbHeight = (this.querySelector(".swiper-slide").clientHeight + 16) * this.querySelectorAll(".swiper-slide").length;
      const thumbsWrapper = this.parentElement;
      function setThumbsWrapperHeightToAuto() {
        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = (calculateAllThumbHeight - 16) + "px";
        } else {
          thumbsWrapper.removeAttribute("style");
        }
      }
      window.addEventListener("resize", setThumbsWrapperHeightToAuto);
      // have to wait global.js media area height css variables to be generated
      setTimeout(() => {
        setThumbsWrapperHeightToAuto();
      }, 2500);
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit,
      },
    };
  }

}
if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute("thumbs");
    this.isZoomActive = this.hasAttribute("zoom");
  }

  static get observedAttributes() {
    return ["thumbs", "zoom"];
  }
    
  setOptions() {
    super.setOptions();
    this.setThumbOptions();

    const desktopOptions = this._options.breakpoints?.[750] || {};

    this._options = {
      ...this._options,

      // Mobile: one full image + next image preview
      slidesPerView: 1.2,
      slidesPerGroup: 1,
      spaceBetween: 0,

      loop: false,
      rewind: false,
      centeredSlides: false,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      watchOverflow: true,

      breakpoints: {
        ...(this._options.breakpoints || {}),
        750: {
          ...desktopOptions,
          slidesPerView: desktopOptions.slidesPerView ?? 1,
          spaceBetween: desktopOptions.spaceBetween ?? 0
        }
      }
    };

    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    if (this.debug) console.log("setThumbOptions");
    const thumbsInstance = super.getThumbsInstance();
    if (this.debug) console.log("thumbsInstance", thumbsInstance);
    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    this.syncGalleryLoadState();
    this.setKeyHighlightsOverlayInteraction();
    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();

    this.setupFiveDotPagination();

    if (window.innerWidth < 750) {
      requestAnimationFrame(() => {
        this.instance.update();
      });
    }

    // Force pagination update on mobile after each slide change
    this.instance.on("slideChange", swiper => {
      if (window.innerWidth < 750 && swiper.pagination) {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated manually on mobile.");
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on("scroll", swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        if (swiper.pagination) swiper.pagination.update();
        if (this.debug) console.log("Pagination updated after scroll.");
      }, 200);
    });
  }

  setupFiveDotPagination() {
    const swiper = this.instance;
    if (!swiper) return;

    const updateDots = () => {
      const pagination = swiper.pagination?.el;
      if (!pagination) return;

      const bullets = Array.from(
        pagination.querySelectorAll(".swiper-pagination-bullet")
      );

      if (!bullets.length) return;

      if (window.innerWidth >= 750) {
        pagination.classList.remove("hs-five-dots-ready");

        bullets.forEach((bullet) => {
          bullet.classList.remove(
            "hs-dot-visible",
            "hs-dot-neighbor"
          );
        });

        return;
      }

      const total = bullets.length;

      const active = Math.max(
        0,
        Math.min(swiper.realIndex, total - 1)
      );

      /* Show 2 dots before and after active */
      const start = Math.max(0, active - 2);
      const end = Math.min(total - 1, active + 2);

      bullets.forEach((bullet, index) => {

        const visible = index >= start && index <= end;

        const neighbor = Math.abs(index - active) === 1;

        bullet.classList.toggle(
          "hs-dot-visible",
          visible
        );

        bullet.classList.toggle(
          "hs-dot-neighbor",
          visible && neighbor
        );

      });

      pagination.classList.add("hs-five-dots-ready");
    };

    updateDots();

    swiper.on("slideChange", updateDots);
    swiper.on("paginationUpdate", updateDots);
    swiper.on("breakpoint", updateDots);

    window.addEventListener("resize", updateDots);
  }

  syncGalleryLoadState() {
    const mediaRoot = this.closest('.main-product__media');
    if (!mediaRoot || mediaRoot.dataset.galleryReady === 'true') return;

    const mainImage =
      mediaRoot.querySelector('.swiper--product-gallery .swiper-slide[data-selected] .media > img') ||
      mediaRoot.querySelector('.swiper--product-gallery .swiper-slide[data-selected] .media > a > img') ||
      mediaRoot.querySelector('.swiper--product-gallery .swiper-slide .media > img') ||
      mediaRoot.querySelector('.swiper--product-gallery .swiper-slide .media > a > img');

    const thumbImage =
      mediaRoot.querySelector('.swiper--product-thumbs .swiper-slide[data-selected] .media > img') ||
      mediaRoot.querySelector('.swiper--product-thumbs .swiper-slide .media > img');

    const criticalImages = [mainImage, thumbImage].filter((img, index, arr) => img && arr.indexOf(img) === index);

    const markReady = () => {
      if (mediaRoot.dataset.galleryReady === 'true') return;
      mediaRoot.dataset.galleryReady = 'true';
      mediaRoot.classList.remove('main-product__media--loading');
      mediaRoot.classList.add('main-product__media--ready');
    };

    if (!criticalImages.length) {
      markReady();
      return;
    }

    let pending = 0;
    criticalImages.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) return;
      pending += 1;

      const onDone = () => {
        img.removeEventListener('load', onDone);
        img.removeEventListener('error', onDone);
        pending -= 1;
        if (pending <= 0) markReady();
      };

      img.addEventListener('load', onDone, { once: true });
      img.addEventListener('error', onDone, { once: true });
    });

    if (pending === 0) {
      markReady();
      return;
    }

    window.setTimeout(markReady, 1200);
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    if (this.debug) console.log("setThumbsInteraction");
    const thumbsSwiper = this.options.thumbs.swiper;
    this.instance.on("slideChangeTransitionStart", function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (this.debug) console.log("slideChangeTransitionStart", activeIndex, thumbsActiveIndex);
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  setKeyHighlightsOverlayInteraction() {
    const setOverlayCollapsed = (overlay, shouldCollapse) => {
      const toggle = overlay.querySelector("[data-key-highlights-toggle]");

      overlay.classList.toggle("is-collapsed", shouldCollapse);
      toggle?.setAttribute("aria-label", shouldCollapse ? "Show key highlights" : "Hide key highlights");
    };

    this.addEventListener("click", event => {
      const seeMore = event.target.closest("[data-key-highlights-see-more]");
      const toggle = event.target.closest("[data-key-highlights-toggle]");
      const media = event.target.closest(".media");
      const overlay =
        event.target.closest("[data-key-highlights-overlay]") ||
        media?.querySelector("[data-key-highlights-overlay]");
      if (!overlay) return;

      if (seeMore) {
        event.preventDefault();
        event.stopPropagation();

        const isVisible = element => {
          if (!element) return false;
          const styles = window.getComputedStyle(element);
          return element.getClientRects().length > 0 && styles.display !== "none" && styles.visibility !== "hidden";
        };
        const keyHighlightsTargets = [...document.querySelectorAll("[data-key-highlights-accordion]")];
        const target =
          keyHighlightsTargets.find(isVisible) ||
          keyHighlightsTargets[0] ||
          document.querySelector(seeMore.hash);

        setOverlayCollapsed(overlay, true);

        if (!target) return;

        const tabPanel = target.classList.contains("product-tabs__panel")
          ? target
          : target.closest(".product-tabs__panel");
        if (tabPanel?.dataset.tab) {
          tabPanel
            .closest(".product-tabs__layout")
            ?.querySelector(`.product-tabs__tab[data-tab="${tabPanel.dataset.tab}"]`)
            ?.click();
        }

        const accordion = target.tagName === "DETAILS" ? target : target.closest("details");
        if (accordion && !accordion.open) {
          accordion.querySelector("summary")?.click();
        }

        window.requestAnimationFrame(() => {
          const titleTarget =
            accordion?.querySelector("summary") ||
            tabPanel
              ?.closest(".product-tabs__layout")
              ?.querySelector(`.product-tabs__tab[data-tab="${tabPanel.dataset.tab}"]`) ||
            target;
          const targetTop = titleTarget.getBoundingClientRect().top + window.pageYOffset;
          const scrollTop = Math.max(0, targetTop - window.innerHeight * 0.35);

          window.scrollTo({ top: scrollTop, behavior: "smooth" });
        });
        return;
      }

      if (toggle) {
        event.preventDefault();
        event.stopPropagation();

        const isCollapsed = !overlay.classList.contains("is-collapsed");
        setOverlayCollapsed(overlay, isCollapsed);
        return;
      }

      if (!overlay.classList.contains("is-collapsed")) {
        event.preventDefault();
        event.stopPropagation();

        setOverlayCollapsed(overlay, true);
      }
    });
  }

  photoSwipeLightboxInit() {
    if (typeof PhotoSwipeLightbox === "undefined" || typeof PhotoSwipe === "undefined") return;
    const [
      zoomContainerWidth,
      zoomContainerHeight,
      closeIcon,
      prevIcon,
      nextIcon
    ] = [
      this.offsetWidth,
      this.offsetHeight,
      document.querySelector("[data-close-icon]"),
      document.querySelector("[data-prev-icon]"),
      document.querySelector("[data-next-icon]")
    ];

    function isPhone() {
      return window.innerWidth < 750;
    }

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `.photoswipe-wrapper`,
      children: "a.product__gallery-toggle",
      mainClass: "pswp--product-media-gallery",

      loop: false,
      showHideAnimationType: "zoom",

      initialZoomLevel: zoomLevelObject => {
        if (isPhone()) {
          return zoomLevelObject.vFill;
        } else {
          return zoomLevelObject.fit;
        }
      },
      secondaryZoomLevel: zoomLevelObject => {
        if (isPhone()) {
          return zoomLevelObject.fit;
        } else {
          return 1;
        }
      },
      pswpModule: PhotoSwipe
    });
    photoSwipeLightboxInstance.addFilter(
      "uiElement",
      (element, data) => {
        if (data.name === "close") {
          element.innerHTML = closeIcon.innerHTML;
        } else if (data.name === "arrowPrev") {
          element.innerHTML = prevIcon.innerHTML;
        } else if (data.name === "arrowNext") {
          element.innerHTML = nextIcon.innerHTML;
        }
        return element;
      }
    );

    // html for video
    photoSwipeLightboxInstance.addFilter(
      "itemData",
      (itemData, index) => {
        if (itemData.type === "html" && itemData.element) {
          return {
            html: itemData.element.dataset.pswpHtml
          };
        }
        return itemData;
      }
    );

    photoSwipeLightboxInstance.init();

    photoSwipeLightboxInstance.on("beforeOpen", () => {
      document.body.classList.add("oveflow-hidden");
      const videos = this.querySelectorAll("video");
      Array.from(videos).forEach(video => {
        // if video is not playing, call playPromise
        // force to play
        video
          .play()
          .then(() => {
            // Automatic playback started!
            // Show playing UI.
            // video.play();
          })
          .catch(error => {
            // Auto-play was prevented
            // Show paused UI.
            video.pause();
          });
      });
    });

    photoSwipeLightboxInstance.on("closingAnimationStart", () => {
      document.body.classList.remove("oveflow-hidden");
    });
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(
      this.querySelectorAll("[data-media-id]")
    ).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}


class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();
    this.init();
    window.addEventListener("resize", this.init.bind(this));
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider').offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && window.innerWidth > 750) {
      containerOffsetWidth = document.querySelector('.main-product__media--grid-item').offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const mediaInfoTextWidth = this.querySelector('p:last-child').offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.setProperty('--marquee-speed', `${mediaInfoTextWidth / maxWidthForInfo * 8}s`);
      mediaInfoHidden.classList.remove('hidden');
    } else {
      this.classList.add('animation-stopped');
      this.style.removeProperty('--marquee-speed');
      mediaInfoHidden.classList.add('hidden');
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}


